import { Article } from 'types/blog';

// ==============================|| 記事本文の読み込み ||============================== //
//
// 記事の本文は、その記事を開いた人にだけ配る。
//
// ■ なぜこうしているか
//   記事データを1つの配列に静的 import すると、webpack はそれを1つのJSにまとめる。
//   すると /blog の一覧を開いただけで全記事の本文がダウンロードされ、
//   記事を1本増やすたびにサイトの全ページが重くなっていた。
//   （日本語はバンドル内で \uXXXX に展開されるため、ソースの倍のバイト数を食う）
//
//   一覧に必要なのは見出しだけなので、メタ(ARTICLE_META)と本文を別ファイルに分け、
//   本文はここから動的 import する。これで
//     - /blog（一覧・カテゴリ一覧） … メタだけ
//     - /blog/<slug>（記事）        … メタ + その記事の本文だけ
//   になる。記事を何本増やしても、読む人が読み込む量は変わらない。
//
// ■ プリレンダリングとの関係（重要）
//   ビルド時に scripts/prerender.js が各記事を描画済みHTMLとして書き出しており、
//   ブラウザはそのHTMLを hydrateRoot で引き継ぐ。このとき本文がまだ読み込めていないと、
//   HTML側の本文とReactが描くものが食い違い、ハイドレーションに失敗する
//   （ページ全体が描き直され、プリレンダの意味が無くなる）。
//
//   そこで src/index.tsx が、記事ページを開いたときだけ
//   hydrateRoot の前に loadArticle() の完了を待つ。待っている間も
//   プリレンダ済みのHTMLがそのまま表示されているので、見た目には何も起きない。
//
// ■ 記事を増やすときは ARTICLE_LOADERS に1行足す
//   ここを書き忘れると、記事ページが 404 になる（一覧には出るのに開けない）。
//   npm run blog:map が meta と loader の食い違いを検出する。

/**
 * slug → 本文を読み込む関数。
 *
 * import() の中身は必ず文字列リテラルで書くこと。変数で組み立てると
 * webpack がどのファイルを切り出せばよいか判断できず、
 * articles ディレクトリ全体が1つのチャンクにまとめられてしまう。
 */
const ARTICLE_LOADERS: Record<string, () => Promise<Article>> = {
    'kobetsu-shien-keikaku-kakikata': () =>
        import('./articles/kobetsu-shien-keikaku-kakikata').then((m) => m.kobetsuShienKeikakuKakikata),
    'kobetsu-shien-keikaku-misakusei-gensan': () =>
        import('./articles/kobetsu-shien-keikaku-misakusei-gensan').then((m) => m.kobetsuShienKeikakuMisakuseiGensan),
    'gensan-ichiran': () => import('./articles/gensan-ichiran').then((m) => m.gensanIchiran),
    'shien-program-mikouhyou-gensan': () =>
        import('./articles/shien-program-mikouhyou-gensan').then((m) => m.shienProgramMikouhyouGensan),
    'jiko-hyoka-mikouhyou-gensan': () => import('./articles/jiko-hyoka-mikouhyou-gensan').then((m) => m.jikoHyokaMikouhyouGensan),
    'jihatsukan-ketsujo-gensan': () => import('./articles/jihatsukan-ketsujo-gensan').then((m) => m.jihatsukanKetsujoGensan),
    'jouhou-kouhyou-mihoukoku-gensan': () =>
        import('./articles/jouhou-kouhyou-mihoukoku-gensan').then((m) => m.jouhouKouhyouMihoukokuGensan),
    'bcp-misakutei-gensan': () => import('./articles/bcp-misakutei-gensan').then((m) => m.bcpMisakuteiGensan),
    'gyakutai-boushi-mijisshi-gensan': () =>
        import('./articles/gyakutai-boushi-mijisshi-gensan').then((m) => m.gyakutaiBoushiMijisshiGensan),
    'shintai-kousoku-mijisshi-gensan': () =>
        import('./articles/shintai-kousoku-mijisshi-gensan').then((m) => m.shintaiKousokuMijisshiGensan)
};

/**
 * 読み込み済みの本文。
 *
 * 同じ記事を2度読み込まないためと、描画時に同期で取り出すために持っている。
 * 記事ページのコンポーネントは、この Map から同期的に引けることを前提に描く
 * （引けなければ読み込み中と判断する）。
 */
const loaded = new Map<string, Article>();

/** その slug の記事が存在するか（本文を読み込まずに判定できる） */
export const hasArticle = (slug?: string): boolean => Boolean(slug && slug in ARTICLE_LOADERS);

/** 読み込み済みの本文を同期で取り出す。まだなら undefined */
export const getLoadedArticle = (slug?: string): Article | undefined => (slug ? loaded.get(slug) : undefined);

/**
 * 本文を読み込む。読み込み済みならそのまま返す。
 * 存在しない slug のときは undefined を返す（例外にはしない。呼び出し側が404を出す）。
 */
export const loadArticle = async (slug?: string): Promise<Article | undefined> => {
    if (!slug) {
        return undefined;
    }
    const cached = loaded.get(slug);
    if (cached) {
        return cached;
    }
    const loader = ARTICLE_LOADERS[slug];
    if (!loader) {
        return undefined;
    }
    const article = await loader();
    loaded.set(slug, article);
    return article;
};

/** 登録されている slug の一覧。meta との食い違いを検査するために使う */
export const registeredSlugs = (): string[] => Object.keys(ARTICLE_LOADERS);
